// === Phase 5.4+ === ActionPicker popover for the +Add chip on TriggerNode /
// ActionNode. Renders a small fixed-position menu anchored to the chip and
// portalled to document.body so React Flow's overflow:hidden does not clip it.
// Click a row → onPick(kind) then onClose(). Click outside → onClose().
import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { defaultParamsFor } from "../../../plugins/InteractivePlugin/components/InteractivePluginRenderer";

// Interaction rows live at the top and all carry kind="showInteraction" with an
// `itemKind` extra. The bridge inlines the interaction definition onto the
// showInteraction action itself (as `interactionKind` + `params`) rather than
// creating a separate InteractivePlugin item — buildGraph reads those fields to
// emit branchingQuestionNode / endScreenNode and the Visor reads them at runtime.
const KINDS = [
  // --- Interactions (group header rendered inline by `header:true`) ---
  { header: true, label: "Interactions" },
  {
    kind: "showInteraction",
    itemKind: "MCQ",
    label: "Ask Quiz",
    hint: "Multiple choice question",
    icon: "🎯",
  },
  {
    kind: "showInteraction",
    itemKind: "FlipCard",
    label: "Show FlipCard",
    hint: "Two-sided card",
    icon: "🃏",
  },
  {
    kind: "showInteraction",
    itemKind: "YesNo",
    label: "Yes/No",
    hint: "Yes or no prompt",
    icon: "✅",
  },
  {
    kind: "showInteraction",
    itemKind: "TrueFalse",
    label: "True/False",
    hint: "True or false prompt",
    icon: "🔘",
  },
  {
    kind: "showInteraction",
    itemKind: "DragDrop",
    label: "Drag & Drop",
    hint: "Place items on targets",
    icon: "🖱",
  },
  {
    kind: "showInteraction",
    itemKind: "Matching",
    label: "Matching",
    hint: "Pair items left↔right",
    icon: "🔗",
  },
  {
    kind: "showInteraction",
    itemKind: "FillInBlank",
    label: "Fill in the blank",
    hint: "Type the answer",
    icon: "✍",
  },
  {
    kind: "showInteraction",
    itemKind: "Ordering",
    label: "Ordering",
    hint: "Put items in order",
    icon: "🔢",
  },
  {
    kind: "showInteraction",
    itemKind: "MultipleAnswer",
    label: "Multiple Answer",
    hint: "Pick all that apply",
    icon: "☑",
  },
  {
    kind: "showInteraction",
    itemKind: "BranchingQuestion",
    label: "Branching Question",
    hint: "Multi-path fork (H5P-style)",
    icon: "call_split",
  },
  {
    kind: "showInteraction",
    itemKind: "EndScreen",
    label: "End Screen",
    hint: "Terminal destination (H5P-style)",
    icon: "flag",
  },
  // --- Actions ---
  { header: true, label: "Actions" },
  { kind: "resume", label: "Continue", hint: "Move to the next node", icon: "▶" },
  { kind: "showText", label: "Show text", hint: "Display a short message", icon: "💬" },
  { kind: "showImage", label: "Show image", hint: "Display an image overlay", icon: "🖼" },
  { kind: "playVideo", label: "Play video", hint: "Play a short clip", icon: "▷" },
  { kind: "setVar", label: "Set variable", hint: "Update or compute a value", icon: "=" },
  { kind: "jumpScene", label: "Jump to scene", hint: "Go to a different scene", icon: "→" },
  { kind: "replayItem", label: "Replay", hint: "Let the learner try again", icon: "↺" },
  { kind: "wait", label: "Wait", hint: "Pause for N milliseconds", icon: "⏱" },
  {
    kind: "branch",
    label: "Branch (if)",
    hint: "Run different actions per condition",
    icon: "?",
  },
  { kind: "showBox", label: "Show element", hint: "Reveal a hidden scene element", icon: "+" },
  { kind: "hideBox", label: "Hide element", hint: "Hide a scene element", icon: "−" },
];

export default function ActionPicker({ anchorEl, onPick, onClose }) {
  const menuRef = useRef(null);
  const [pos, setPos] = useState(() => computePos(anchorEl));

  // Recompute when anchor changes (mount path).
  useEffect(() => {
    setPos(computePos(anchorEl));
  }, [anchorEl]);

  // Outside-click + Escape close.
  useEffect(() => {
    const handleMouseDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        onClose && onClose();
      }
    };
    const handleKey = (e) => {
      if (e.key === "Escape") onClose && onClose();
    };
    document.addEventListener("mousedown", handleMouseDown, true);
    document.addEventListener("keydown", handleKey, true);
    return () => {
      document.removeEventListener("mousedown", handleMouseDown, true);
      document.removeEventListener("keydown", handleKey, true);
    };
  }, [onClose]);

  if (typeof document === "undefined") return null;

  const node = (
    <div
      ref={menuRef}
      className="action-picker"
      style={{ top: pos.top, left: pos.left }}
      role="menu"
      data-testid="action-picker"
    >
      {KINDS.map((k, i) => {
        if (k.header) {
          return (
            <div
              key={`hdr_${i}`}
              className="action-picker__header"
              data-testid={`action-picker__header-${k.label}`}
            >
              {k.label}
            </div>
          );
        }
        const testId =
          k.kind === "showInteraction" ? `action-picker__row-${k.itemKind}` : undefined;
        return (
          <button
            key={`${k.kind}_${k.itemKind || i}`}
            type="button"
            className="action-picker__row"
            data-kind={k.kind}
            data-item-kind={k.itemKind || undefined}
            data-testid={testId}
            onClick={(e) => {
              e.stopPropagation();
              // For interaction rows we also pass the resolved default `params`
              // for that itemKind so the bridge can INLINE the interaction
              // definition on the showInteraction action without round-tripping
              // through the InteractivePlugin item registry.
              const extras = k.itemKind
                ? { itemKind: k.itemKind, params: defaultParamsFor(k.itemKind) }
                : undefined;
              onPick && onPick(k.kind, extras);
              onClose && onClose();
            }}
            role="menuitem"
          >
            <span className="action-picker__icon" aria-hidden="true">
              {k.icon}
            </span>
            <span className="action-picker__text">
              <span className="action-picker__label">{k.label}</span>
              <span className="action-picker__hint">{k.hint}</span>
            </span>
          </button>
        );
      })}
    </div>
  );

  return createPortal(node, document.body);
}

function computePos(anchorEl) {
  if (!anchorEl || typeof anchorEl.getBoundingClientRect !== "function") {
    return { top: 0, left: 0 };
  }
  const rect = anchorEl.getBoundingClientRect();
  // Place to the right of the chip, vertically centered. Clamp into viewport.
  const menuWidth = 260; // matches min-width 240 + small breathing room
  const menuHeight = 360; // max-height in SCSS
  let left = rect.right + 8;
  let top = rect.top + rect.height / 2 - 24;
  const vw = typeof window !== "undefined" ? window.innerWidth : 1024;
  const vh = typeof window !== "undefined" ? window.innerHeight : 768;
  if (left + menuWidth > vw - 8) left = Math.max(8, rect.left - menuWidth - 8);
  if (top + menuHeight > vh - 8) top = Math.max(8, vh - menuHeight - 8);
  if (top < 8) top = 8;
  return { top, left };
}
// === /Phase 5.4+ ===
